import { ReactNode, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { usePopper } from 'react-popper';
import classnames from 'classnames';
import useStyles, { arrowHeight } from './Tooltip.styles';

const tooltipVisibilityAttr = 'data-show';
const accessibilityAttr = 'tooltip';

type TPlacement = NonNullable<Parameters<typeof usePopper>[2]>['placement'];
export interface ITooltipProps {
  children: ReactNode;
  /** The tooltip content */
  content: ReactNode;
  /** Optional class name for the root tooltip container */
  className?: string;
  /** For custom testing purposes. Will override data-qa attr for the root tooltip container */
  dataQa?: string;
  /** The placement of the tooltip in respect to the hoverable element */
  placement?: TPlacement;
  /** Pre-defined style options for opacity of root tooltip container */
  transparency?: 'solid' | 'semi';
  /** Pre-defined style options for background/color of root tooltip container */
  variant?: 'primary' | 'secondary';
  /** Delay(in ms) to show the content after specific time. */
  delay?: number;
}

const Tooltip = ({
  children,
  className,
  content,
  dataQa = 'tooltip-reference',
  delay = 300,
  placement = 'bottom',
  transparency = 'solid',
  variant = 'primary',
}: ITooltipProps) => {
  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { attributes, styles, update } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: {
          offset: [0, arrowHeight],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          altAxis: true,
          padding: 16,
        },
      },
      {
        name: 'flip',
        options: {
          padding: 16,
        },
      },
    ],
  });
  const classes = useStyles(useMemo(() => ({ variant, transparency }), [variant, transparency]));

  const show = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      popperElement?.setAttribute(tooltipVisibilityAttr, '');
      update?.();
    }, delay);
  }, [delay, popperElement, update]);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      popperElement?.removeAttribute(tooltipVisibilityAttr);
    }, 0);
  }, [popperElement]);

  const handleKeyDown = useCallback(
    ({ keyCode }: { keyCode: number }) => {
      // esc or tab out hides tooltip
      if (keyCode === 27 || keyCode === 9) {
        hide();
      }
    },
    [hide]
  );

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        ref={setReferenceElement}
        aria-describedby={accessibilityAttr}
        onBlur={hide}
        onFocus={show}
        onKeyDown={handleKeyDown}
        onMouseEnter={show}
        onMouseLeave={hide}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        {children}
      </span>
      <div
        ref={setPopperElement}
        className={classnames(classes.root, className)}
        data-qa={dataQa}
        id={accessibilityAttr}
        style={styles.popper}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...attributes.popper}
      >
        {content}
        <div ref={setArrowElement} className={classes.arrow} style={styles.arrow} />
      </div>
    </>
  );
};

export default Tooltip;
